import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie, X } from "lucide-react";

/**
 * Consent + granular preferences center.
 *
 * Two levels:
 *  1. Compact banner asks for accept-all / customise.
 *  2. Customise opens an in-place tray with per-category toggles that write
 *     to `localStorage.gm_consent_prefs` as JSON. Downstream loaders (e.g.
 *     Microsoft Clarity) can `window.gmConsent.get()` to check.
 */
const STORAGE_KEY = "gm_consent_prefs";
const CATS = [
  {
    id: "necessary",
    label: "Strictly necessary",
    desc: "Required for the site to work — session, security, signup form. Always on.",
    locked: true,
  },
  {
    id: "analytics",
    label: "Product analytics",
    desc: "Anonymised page + event counts so we know what's working. No ad IDs.",
    locked: false,
  },
  {
    id: "session_recording",
    label: "Session recording",
    desc: "Microsoft Clarity replays with faces + inputs masked. Helps us fix bugs.",
    locked: false,
  },
];

const defaultPrefs = { necessary: true, analytics: true, session_recording: true };
const rejectedPrefs = { necessary: true, analytics: false, session_recording: false };

function readPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return { ...defaultPrefs, ...JSON.parse(raw) };
  } catch {
    return null;
  }
}

function writePrefs(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    // Legacy flag so any older consumer still works.
    localStorage.setItem("gm_consent", "1");
    window.gmConsent = { get: () => ({ ...prefs }) };
    window.dispatchEvent(new CustomEvent("gm-consent-changed", { detail: prefs }));
    if (prefs.analytics || prefs.session_recording) {
      window.dispatchEvent(new Event("gm-consent-accepted"));
    }
  } catch {}
}

export default function ConsentBanner() {
  const [show, setShow] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [managing, setManaging] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);
  const { pathname } = useLocation();

  // Initial mount: read stored prefs, show banner only if none exist. Defer the
  // first paint cost until after load so the banner never delays LCP — it isn't
  // needed for the visitor to start reading the hero.
  useEffect(() => {
    const init = () => {
      const stored = readPrefs();
      if (stored) {
        setPrefs(stored);
        window.gmConsent = { get: () => ({ ...stored }) };
      } else {
        setShow(true);
      }
    };
    if (document.readyState === "complete") init();
    else {
      window.addEventListener("load", init, { once: true });
      return () => window.removeEventListener("load", init);
    }
  }, []);

  // Listen for global open request from footer link
  useEffect(() => {
    const handler = () => {
      const stored = readPrefs() || defaultPrefs;
      setPrefs(stored);
      setManaging(true);
      setShow(true);
    };
    window.addEventListener("gm-open-consent", handler);
    return () => window.removeEventListener("gm-open-consent", handler);
  }, []);

  // Hide while any Radix Dialog is open (they set data-scroll-locked on body).
  useEffect(() => {
    if (typeof document === "undefined") return;
    const body = document.body;
    const sync = () => setDialogOpen(body.hasAttribute("data-scroll-locked"));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(body, { attributes: true, attributeFilter: ["data-scroll-locked"] });
    return () => observer.disconnect();
  }, []);

  if (!show || dialogOpen) return null;
  if (pathname.startsWith("/app") || pathname.startsWith("/e/")) return null;

  const acceptAll = () => { writePrefs(defaultPrefs); setShow(false); setManaging(false); };
  const rejectAll = () => { writePrefs(rejectedPrefs); setShow(false); setManaging(false); };
  const savePrefs = () => { writePrefs(prefs); setShow(false); setManaging(false); };

  if (managing) {
    return (
      <div
        data-testid="consent-manager"
        className="fixed bottom-4 inset-x-4 md:left-auto md:right-6 md:max-w-md z-[70] glass rounded-2xl p-5 shadow-2xl border border-white/10"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="overline">Privacy preferences</p>
            <p className="text-sm text-zinc-400 mt-1">
              Choose exactly what you're okay with. You can change this anytime.
            </p>
          </div>
          <button
            onClick={() => setManaging(false)}
            data-testid="consent-manager-close"
            aria-label="Close"
            className="text-zinc-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-3">
          {CATS.map((c) => (
            <div key={c.id} className="flex items-start justify-between gap-4" data-testid={`consent-cat-${c.id}`}>
              <div>
                <p className="text-sm font-semibold text-white">{c.label}</p>
                <p className="text-xs text-zinc-500 leading-relaxed">{c.desc}</p>
              </div>
              <Switch
                checked={c.locked ? true : !!prefs[c.id]}
                disabled={c.locked}
                onCheckedChange={(v) => setPrefs({ ...prefs, [c.id]: v })}
                data-testid={`consent-switch-${c.id}`}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-between items-center gap-2 mt-5">
          <button
            onClick={rejectAll}
            data-testid="consent-reject-all"
            className="text-xs text-zinc-400 hover:text-white"
          >
            Reject non-essential
          </button>
          <Button
            onClick={savePrefs}
            data-testid="consent-save"
            className="rounded-full h-9 px-5 bg-gradient-to-r from-neon-violet to-neon-cyan text-white text-sm font-semibold border-0"
          >
            Save preferences
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="consent-banner"
      className="fixed bottom-4 inset-x-4 md:left-auto md:right-6 md:max-w-md z-[70] glass glass-cyan rounded-2xl p-5 shadow-2xl"
    >
      <div className="flex items-start gap-3">
        <Cookie className="w-5 h-5 text-neon-cyan shrink-0 mt-0.5" />
        <div className="text-sm text-zinc-300 leading-relaxed">
          We use privacy-friendly analytics and anonymous session recordings to improve the site. No ads, no data resale.{" "}
          <Link to="/privacy-policy" className="text-neon-cyan underline underline-offset-2">Privacy Policy</Link>.
        </div>
      </div>
      <div className="flex justify-between items-center gap-2 mt-4">
        <button
          onClick={() => setManaging(true)}
          data-testid="consent-manage"
          className="text-xs text-zinc-400 hover:text-white"
        >
          Manage preferences
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={rejectAll}
            data-testid="consent-reject"
            className="text-xs text-zinc-400 hover:text-white px-3 py-2"
          >
            Reject
          </button>
          <Button
            onClick={acceptAll}
            data-testid="consent-accept"
            className="rounded-full h-9 px-5 bg-gradient-to-r from-neon-violet to-neon-cyan text-white text-sm font-semibold border-0"
          >
            Accept all
          </Button>
        </div>
      </div>
    </div>
  );
}
